import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Tractor, Search, Loader2, MapPin, Package, Send, X, Sprout } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MARKET_COLLECTIONS = [
  { title: 'Fresh Produce', description: 'Harvest-led vegetables, fruits, grains and farm staples.', icon: '🥬' },
  { title: 'Pantry & Processed', description: 'Reliable processed foods and value-added farm products.', icon: '🫙' },
  { title: 'Wholesale Lots', description: 'Bulk-ready commodities for retailers, kitchens and distributors.', icon: '📦' },
];

const LoanInterestPanel = () => {
  const [form, setForm] = useState({ cooperative_name: '', contact_name: '', email: '', phone: '', location: '', requested_amount: '', notes: '', website: '' });
  const [submitting, setSubmitting] = useState(false);
  const update = (field, value) => setForm((current) => ({ ...current, [field]: value }));
  const submit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/farmer-cooperative-loan-interest`, { ...form, requested_amount: Number(form.requested_amount) });
      toast.success(response.data.message);
      setForm({ cooperative_name: '', contact_name: '', email: '', phone: '', location: '', requested_amount: '', notes: '', website: '' });
    } catch (error) { toast.error(error.response?.data?.detail || 'We could not save your interest request.'); }
    finally { setSubmitting(false); }
  };
  return <section id="cooperative-loan" className="max-w-7xl mx-auto px-4 py-16" data-testid="cooperative-loan-interest-section">
    <div className="grid lg:grid-cols-[1fr_1.1fr] gap-8 bg-emerald-950 text-white p-6 sm:p-10 rounded-lg overflow-hidden">
      <div className="space-y-5"><p className="text-xs font-bold tracking-[0.14em] text-emerald-300">COMING SOON</p><h2 className="text-3xl sm:text-4xl font-bold">Farmer Cooperative Loan</h2><p className="text-emerald-100 leading-relaxed">We are preparing a cooperative-focused finance program for farmers ready to invest in equipment, inputs and growth together.</p><div className="grid grid-cols-2 gap-3 text-sm"><div className="border border-emerald-800 p-3">Cooperative-first</div><div className="border border-emerald-800 p-3">Built for farm growth</div></div></div>
      <form onSubmit={submit} className="bg-white text-gray-900 p-5 sm:p-6 rounded-md grid sm:grid-cols-2 gap-3" data-testid="cooperative-loan-interest-form">
        <div className="sm:col-span-2"><h3 className="font-bold text-lg">Register your cooperative’s interest</h3><p className="text-sm text-gray-500 mt-1">We’ll contact you when applications open.</p></div>
        <Input required value={form.cooperative_name} onChange={(e) => update('cooperative_name', e.target.value)} placeholder="Cooperative name" data-testid="loan-interest-cooperative-name" />
        <Input required value={form.contact_name} onChange={(e) => update('contact_name', e.target.value)} placeholder="Contact person" data-testid="loan-interest-contact-name" />
        <Input required type="email" value={form.email} onChange={(e) => update('email', e.target.value)} placeholder="Email address" data-testid="loan-interest-email" />
        <Input required value={form.phone} onChange={(e) => update('phone', e.target.value)} placeholder="Phone number" data-testid="loan-interest-phone" />
        <Input required value={form.location} onChange={(e) => update('location', e.target.value)} placeholder="State / location" data-testid="loan-interest-location" />
        <Input required min="1" type="number" value={form.requested_amount} onChange={(e) => update('requested_amount', e.target.value)} placeholder="Estimated amount (₦)" data-testid="loan-interest-amount" />
        <Textarea className="sm:col-span-2" maxLength={1500} value={form.notes} onChange={(e) => update('notes', e.target.value)} placeholder="What would the loan help your cooperative achieve? (optional)" data-testid="loan-interest-notes" />
        <input className="hidden" tabIndex="-1" aria-hidden="true" autoComplete="off" value={form.website} onChange={(e) => update('website', e.target.value)} data-testid="loan-interest-website" />
        <Button type="submit" disabled={submitting} className="sm:col-span-2 bg-green-700 hover:bg-green-800" data-testid="loan-interest-submit-button">{submitting ? 'Saving interest…' : 'Register interest'}</Button>
      </form>
    </div>
  </section>;
};

const CommodityCard = ({ item, onInquire }) => (
  <Card className="card-hover-lift overflow-hidden" data-testid={`market-item-${item._id}`}>
    {item.photo_url ? (
      <img src={`${BACKEND_URL}${item.photo_url}`} alt={item.name} className="w-full aspect-square object-cover" loading="lazy" />
    ) : (
      <div className="w-full aspect-square bg-green-50 flex items-center justify-center">
        <Sprout className="h-12 w-12 text-green-300" aria-hidden="true" />
      </div>
    )}
    <CardContent className="p-3 space-y-1">
      <div className="flex items-start justify-between gap-2">
        <p className="font-semibold text-sm truncate">{item.name}</p>
        <Badge className="bg-green-100 text-green-800 border-none text-[10px]">{item.category}</Badge>
      </div>
      <p className="text-lg font-bold text-green-700">₦{Number(item.price).toLocaleString()}<span className="text-xs text-gray-500 font-normal">/{item.unit}</span></p>
      <p className="text-xs text-gray-500 truncate flex items-center gap-1"><MapPin className="h-3 w-3" />{item.pickup_location || 'Nigeria'}</p>
      <p className="text-[11px] text-gray-500 truncate">from {item.farm_name || item.farmer_name}</p>
      <Button size="sm" className="w-full mt-2 bg-green-600 hover:bg-green-700" onClick={() => onInquire(item)} data-testid={`inquire-btn-${item._id}`}>
        <Send className="h-3 w-3 mr-1" /> Contact farmer
      </Button>
    </CardContent>
  </Card>
);

const InquiryModal = ({ item, onClose, onSuccess }) => {
  const [msg, setMsg] = useState('');
  const [qty, setQty] = useState('');
  const [phone, setPhone] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  const send = async () => {
    if (!msg.trim()) { toast.error('Message is required'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/farmers-market/${item._id}/inquire`, {
        message: msg, quantity: qty || 0, contact_phone: phone,
      });
      toast.success('Inquiry sent — the farmer will reply directly to your email.');
      onSuccess();
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.info('Please sign in to send an inquiry');
        navigate('/auth?redirect=/farmers-market');
        return;
      }
      toast.error(err?.response?.data?.detail || 'Send failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" data-testid="inquiry-modal">
      <Card className="w-full max-w-md">
        <div className="bg-green-700 text-white p-4 rounded-t-xl relative">
          <button className="absolute top-3 right-3 text-white/80 hover:text-white" onClick={onClose} aria-label="Close"><X className="h-5 w-5" /></button>
          <h3 className="font-bold">Inquire: {item.name}</h3>
          <p className="text-xs text-green-100">from {item.farm_name}</p>
        </div>
        <CardContent className="p-5 space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div><label className="text-xs font-semibold block mb-1">Quantity ({item.unit})</label><Input type="number" min={0} value={qty} onChange={e => setQty(e.target.value)} data-testid="inquiry-qty" /></div>
            <div><label className="text-xs font-semibold block mb-1">Your phone</label><Input value={phone} onChange={e => setPhone(e.target.value)} data-testid="inquiry-phone" /></div>
          </div>
          <div>
            <label className="text-xs font-semibold block mb-1">Message*</label>
            <textarea className="w-full border rounded-md p-2 text-sm min-h-[100px]" maxLength={1500} value={msg} onChange={e => setMsg(e.target.value)} data-testid="inquiry-message" placeholder="Tell the farmer what you need, delivery timelines, etc." />
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
            <Button onClick={send} disabled={submitting} className="flex-1 bg-green-600 hover:bg-green-700" data-testid="inquiry-send-btn">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
              Send inquiry
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const FarmersMarket = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ items: [], categories: [], total: 0 });
  const [filters, setFilters] = useState({ q: '', category: '', location: '' });
  const [marketCollection, setMarketCollection] = useState('all');
  const [inquireItem, setInquireItem] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.q) params.set('q', filters.q);
      if (filters.category) params.set('category', filters.category);
      if (filters.location) params.set('location', filters.location);
      const r = await axios.get(`${API}/farmers-market?${params.toString()}`);
      setData(r.data);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const collectionMatches = (item) => {
    if (marketCollection === 'all') return true;
    const text = `${item.name || ''} ${item.category || ''} ${item.description || ''}`.toLowerCase();
    const terms = {
      fresh: ['fresh', 'vegetable', 'fruit', 'crop', 'grain', 'cassava', 'yam', 'ginger'],
      pantry: ['processed', 'pantry', 'oil', 'flour', 'powder', 'dried'],
      wholesale: ['wholesale', 'bulk', 'lot'],
    };
    return terms[marketCollection].some((term) => text.includes(term));
  };
  const visibleItems = data.items.filter(collectionMatches);

  return (
    <div className="min-h-screen bg-gray-50 pb-16" data-testid="farmers-market-page">
      <div className="bg-gradient-to-br from-green-700 to-green-900 text-white py-12 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 rounded-full px-3 py-1 mb-3 text-sm">
            <Tractor className="h-4 w-4" /> Farmers' Market
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold mb-2">Fresh from Daye Farm Ltd verified farmers</h1>
          <p className="text-green-100 max-w-2xl mx-auto text-sm">Directly connect with Nigerian farmers. Every listing is verified by our team.</p>
        </div>
      </div>

      <section className="max-w-7xl mx-auto px-4 py-10" data-testid="market-collections-section"><div className="grid md:grid-cols-3 gap-4">{MARKET_COLLECTIONS.map((collection, index) => { const key = ['fresh', 'pantry', 'wholesale'][index]; return <button type="button" onClick={() => setMarketCollection(marketCollection === key ? 'all' : key)} className={`border p-5 text-left transition-colors ${marketCollection === key ? 'border-emerald-600 bg-emerald-50' : 'border-emerald-100 bg-white hover:border-emerald-400'}`} key={collection.title} data-testid={`market-collection-${key}`} aria-pressed={marketCollection === key}><span className="text-2xl" aria-hidden="true">{collection.icon}</span><h2 className="font-bold text-lg mt-3">{collection.title}</h2><p className="text-sm text-gray-600 mt-1">{collection.description}</p></button>; })}</div></section>

      <div className="max-w-7xl mx-auto px-4 -mt-6">
        <Card>
          <CardContent className="p-3 flex flex-col sm:flex-row gap-2">
            <div className="relative flex-1">
              <Search className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" aria-hidden="true" />
              <Input
                placeholder="Search commodities, farms..."
                className="pl-9"
                value={filters.q}
                onChange={e => setFilters(f => ({...f, q: e.target.value}))}
                data-testid="market-search"
              />
            </div>
            <select
              value={filters.category}
              onChange={e => setFilters(f => ({...f, category: e.target.value}))}
              className="border rounded-md px-3 py-2 text-sm"
              data-testid="market-category-filter"
            >
              <option value="">All categories</option>
              {data.categories.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <Input placeholder="Location" value={filters.location} onChange={e => setFilters(f => ({...f, location: e.target.value}))} className="sm:w-40" data-testid="market-location-filter" />
          </CardContent>
        </Card>
      </div>

      <div className="max-w-7xl mx-auto px-4 mt-6">
        {loading ? (
          <div className="p-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-green-600" /></div>
        ) : visibleItems.length === 0 ? (
          <Card><CardContent className="p-16 text-center">
            <Package className="h-12 w-12 text-gray-300 mx-auto mb-2" />
            <p className="text-gray-500">No commodities match your filters yet.</p>
          </CardContent></Card>
        ) : (
          <>
            <p className="text-sm text-gray-500 mb-3" data-testid="market-listing-count">{visibleItems.length} listing{visibleItems.length !== 1 ? 's' : ''}</p>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {visibleItems.map(item => <CommodityCard key={item._id} item={item} onInquire={setInquireItem} />)}
            </div>
          </>
        )}
      </div>

      <LoanInterestPanel />

      {inquireItem && (
        <InquiryModal item={inquireItem} onClose={() => setInquireItem(null)} onSuccess={() => setInquireItem(null)} />
      )}
    </div>
  );
};

export default FarmersMarket;
